import { useState, useEffect, useCallback } from 'react';
import { useSyncExternalStore } from 'react';
import { getAmplifyClient } from '../lib/amplifyClient';
import { importSummaryStore } from '../stores/importSummaryStore';

export interface ImportMonthSummary {
  yearMonth: string;
  importDate: string;
  hoursCount: number;
  totalConsumption: number;
  isExtrapolated: boolean;
}

export interface ImportSummaryResult {
  success: boolean;
  year: number;
  totalMonths: number;
  imports: ImportMonthSummary[];
  message?: string;
}

export interface UseGetImportSummaryReturn {
  data: ImportSummaryResult | null;
  loading: boolean;
  error: string | null;
  refetch: (year: number) => Promise<void>;
}

export function useGetImportSummary(year: number): UseGetImportSummaryReturn {
  const [data, setData] = useState<ImportSummaryResult | null>(null);
  // Start loading immediately when year is provided — prevents a false "no data" flash
  // before the first fetch completes.
  const [loading, setLoading] = useState(() => !!year);
  const [error, setError] = useState<string | null>(null);

  // Re-fetch automatically whenever any import completes (cross-component invalidation).
  const invalidationVersion = useSyncExternalStore(
    importSummaryStore.subscribe,
    importSummaryStore.getSnapshot
  );

  const fetchSummary = useCallback(async (yearToFetch: number) => {
    setLoading(true);
    setError(null);

    try {
      const client = getAmplifyClient();
      const inputJson = JSON.stringify({ year: yearToFetch });

      const result = await client.queries.getImportSummary({
        input: inputJson,
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data) {
        throw new Error('No data returned from getImportSummary');
      }

      // Parse the AWSJSON response with type guard
      // AppSync may return already-parsed object or stringified JSON
      let parsedResult: any;

      if (typeof result.data === 'string') {
        // Case 1: String response - parse it
        try {
          parsedResult = JSON.parse(result.data);

          // Case 1a: Double-stringified - parse again
          if (typeof parsedResult === 'string') {
            parsedResult = JSON.parse(parsedResult);
          }
        } catch (parseError) {
          throw new Error(
            `Failed to parse response: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`
          );
        }
      } else if (typeof result.data === 'object' && result.data !== null) {
        // Case 2: Already parsed object - use directly
        parsedResult = result.data;
      } else {
        // Case 3: Unexpected type
        throw new Error(
          `Unexpected response type: ${typeof result.data}. Expected string or object.`
        );
      }

      const typedResult: ImportSummaryResult = parsedResult;

      setData(typedResult);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  // State setters and getAmplifyClient are stable — empty dep array is correct.
  }, []);

  useEffect(() => {
    if (year) {
      void fetchSummary(year);
    }
  // invalidationVersion triggers a re-fetch after any successful import.
  }, [year, fetchSummary, invalidationVersion]);

  return {
    data,
    loading,
    error,
    refetch: fetchSummary,
  };
}
